import { useQuery } from "@tanstack/react-query"
import { Case } from "@/types" // Assuming Case type is defined in @/types
import { auth } from "../lib/firebase"

const fetchCaseById = async (id: string): Promise<Case> => {
	const user = auth.currentUser
	if (!user) {
		throw new Error("User not found.")
	}
	const token = await user.getIdToken()
	const response = await fetch(
		`${import.meta.env.VITE_API_URL}/api/cases/${id}/`,
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}
	)
	if (!response.ok) {
		// Consider more specific error handling based on status code if needed
		throw new Error(`HTTP error! status: ${response.status}`)
	}
	const data = await response.json()
	return data
}

/**
 * Hook to fetch a single case by its ID.
 * @param id The ID of the case to fetch. The query is disabled if id is null or undefined.
 * @returns The react-query query object for the case.
 */
export const useCaseById = (id: string | null | undefined) => {
	return useQuery<Case, Error>({
		// Query key includes "case" and the specific id for caching purposes
		queryKey: ["case", id],
		// The function to fetch the data; only called if id is truthy (due to 'enabled' option)
		queryFn: () => fetchCaseById(id!), // Use non-null assertion because 'enabled' guarantees id is defined
		// Disable the query if id is not provided (null or undefined)
		enabled: !!id,
	})
}
